import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import API from '../lib/api';

const AuditContext = createContext(null);

export function AuditProvider({ children }) {
  const [auditRun, setAuditRun] = useState(null);
  const [auditRuns, setAuditRuns] = useState([]);
  const [currentPhase, setCurrentPhase] = useState(1);
  const [newAuditDialogOpen, setNewAuditDialogOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('zentis-theme') || 'light');
  const [loading, setLoading] = useState(false);
  
  const [documents, setDocuments] = useState([]);
  const [controls, setControls] = useState([]);
  const [findings, setFindings] = useState([]);
  const [graphCache, setGraphCache] = useState({});
  const [dataVersion, setDataVersion] = useState(0); // Increment to trigger re-renders

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const { data } = await API.get('/audit-runs');
      setAuditRuns(data);
      return data;
    } catch (e) {
      console.error('Failed to fetch runs:', e);
      return [];
    }
  }, []);

  // Fetch documents for current audit run
  const fetchDocuments = useCallback(async () => {
    if (!auditRun) return [];
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/documents`);
      setDocuments(data);
      return data;
    } catch (e) {
      console.error('Failed to fetch documents:', e);
      return [];
    }
  }, [auditRun]);

  // Fetch controls for current audit run
  const fetchControls = useCallback(async () => {
    if (!auditRun) return [];
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/controls`);
      setControls(data);
      return data;
    } catch (e) {
      console.error('Failed to fetch controls:', e);
      return [];
    }
  }, [auditRun]);

  // Fetch findings for current audit run
  const fetchFindings = useCallback(async () => {
    if (!auditRun) return [];
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/findings`);
      setFindings(data);
      return data;
    } catch (e) {
      console.error('Failed to fetch findings:', e);
      return [];
    }
  }, [auditRun]);

  const refreshRun = useCallback(async () => {
    if (!auditRun) return;
    const { data } = await API.get(`/audit-runs/${auditRun.id}`);
    setAuditRun(data);
    setCurrentPhase(data.current_phase);
    return data;
  }, [auditRun]);

  const triggerDataRefresh = useCallback((actionType) => {
    setDataVersion(v => v + 1);
    setGraphCache({}); // Clear Neo4j graph cache when data is refreshed
    // Refresh specific data based on action type
    if (actionType === 'ingest' || actionType === 'documents') {
      fetchDocuments();
    } else if (actionType === 'extract_controls' || actionType === 'controls') {
      fetchControls();
    } else if (actionType === 'evaluate_evidence' || actionType === 'findings') {
      fetchFindings();
    } else if (actionType === 'all') {
      fetchDocuments();
      fetchControls();
      fetchFindings();
      refreshRun();
    }
  }, [fetchDocuments, fetchControls, fetchFindings, refreshRun]);

  const createRun = useCallback(async (name) => {
    const { data } = await API.post('/audit-runs', { name });
    setAuditRun(data);
    setCurrentPhase(1);
    await fetchRuns();
    return data;
  }, [fetchRuns]);

  const loadRun = useCallback(async (id) => {
    const { data } = await API.get(`/audit-runs/${id}`);
    setAuditRun(data);
    setCurrentPhase(data.current_phase);
    return data;
  }, []);

  const deleteRun = useCallback(async (id) => {
    await API.delete(`/audit-runs/${id}`);
    // Drop the run from cached lists; clear the active run if it was deleted.
    setAuditRuns(prev => prev.filter(run => run.id !== id));
    setAuditRun(prev => (prev?.id === id ? null : prev));
    return true;
  }, []);

  const advancePhase = useCallback(async (nextPhase) => {
    if (!auditRun) return;
    setCurrentPhase(nextPhase);
    setAuditRun(prev => {
      // Mark every phase before the new current phase as complete so no
      // intermediate phase is ever skipped (e.g. 5 -> 7 still completes 6).
      const completed = new Set(prev.phases_completed || []);
      for (let p = 1; p < nextPhase; p += 1) completed.add(p);
      return {
        ...prev,
        current_phase: nextPhase,
        phases_completed: Array.from(completed).sort((a, b) => a - b)
      };
    });
    try {
      await API.put(`/audit-runs/${auditRun.id}/phase`, { phase: nextPhase });
    } catch (e) {
      console.error('Failed to persist phase update:', e);
    }
  }, [auditRun]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.classList.toggle('light', theme !== 'dark');
    localStorage.setItem('zentis-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (auditRun?.id) {
      fetchDocuments();
      fetchControls();
      fetchFindings();
      setGraphCache({});
    } else {
      setDocuments([]);
      setControls([]);
      setFindings([]);
      setGraphCache({});
    }
    // eslint-disable-next-line
  }, [auditRun?.id]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  return (
    <AuditContext.Provider value={{
      auditRun, setAuditRun, auditRuns, setAuditRuns,
      currentPhase, setCurrentPhase,
      newAuditDialogOpen, setNewAuditDialogOpen,
      theme, toggleTheme,
      loading, setLoading,
      documents, controls, findings, graphCache, setGraphCache, dataVersion,
      createRun, loadRun, advancePhase, refreshRun, fetchRuns, deleteRun,
      fetchDocuments, fetchControls, fetchFindings, triggerDataRefresh
    }}>
      {children}
    </AuditContext.Provider>
  );
}

export const useAudit = () => useContext(AuditContext);
